import React, { useState, useMemo } from 'react';
import {
    Autocomplete,
    TextField,
    CircularProgress,
    InputAdornment,
} from '@mui/material';
import { LocationCity } from '@mui/icons-material';
import { useQuery } from '@tanstack/react-query';
import { useDebounce } from '../../utils/debounce';
import api from '../../api/axios';

export interface DistrictOption {
    id: number;
    name: string;
}

interface DistrictAutocompleteProps {
    /** Currently selected district, or null */
    value: DistrictOption | null;
    /** Change handler */
    onChange: (value: DistrictOption | null) => void;
    /** Autocomplete size */
    size?: 'small' | 'medium';
    /** Input label */
    label?: string;
    /** Input placeholder */
    placeholder?: string;
    /** Whether the component is disabled */
    disabled?: boolean;
    /** Error state */
    error?: boolean;
    /** Marks the field required in the UI */
    required?: boolean;
    /** Show the city icon */
    showIcon?: boolean;
    /** Only fetch when enabled (e.g. dialog is open) */
    enabled?: boolean;
    /** Full width by default */
    fullWidth?: boolean;
}

/**
 * Server-searched district picker, mirroring VillageAutocomplete.
 *
 * Districts are searched through the API (Scout) rather than filtered in the
 * browser, so the list is never limited to whatever the first page happened to
 * contain — the reason a plain fetch-100-and-Select hides districts once the
 * table grows past that.
 */
const DistrictAutocomplete: React.FC<DistrictAutocompleteProps> = ({
    value,
    onChange,
    size = 'medium',
    label = 'District',
    placeholder = 'Search districts…',
    disabled = false,
    error = false,
    required = false,
    showIcon = true,
    enabled = true,
    fullWidth = true,
}) => {
    const [inputValue, setInputValue] = useState('');
    const debouncedInput = useDebounce(inputValue, 300);

    const { data: options = [], isLoading } = useQuery({
        queryKey: ['districts-autocomplete', debouncedInput],
        queryFn: async () => {
            const params: any = { per_page: 100 };
            if (debouncedInput) params.search = debouncedInput;
            const res = await api.get('/superadmin/districts', { params });
            return res.data?.data?.data || res.data?.data || [];
        },
        enabled,
    });

    // Keep the selected district visible even when it is absent from the current
    // result page — otherwise editing a record whose district falls outside the
    // fetched page renders an empty field.
    const mergedOptions = useMemo(() => {
        if (!value) return options;
        return options.some((o: DistrictOption) => o.id === value.id)
            ? options
            : [value, ...options];
    }, [options, value]);

    return (
        <Autocomplete
            size={size}
            options={mergedOptions}
            getOptionLabel={(option: DistrictOption) => option.name || ''}
            isOptionEqualToValue={(option, val) => option.id === val.id}
            value={value}
            onChange={(_, newValue) => onChange(newValue)}
            inputValue={inputValue}
            onInputChange={(_, newInputValue) => setInputValue(newInputValue)}
            loading={isLoading}
            // The API has already filtered; re-filtering here would drop matches
            // that the server found on fields the label doesn't show.
            filterOptions={(x) => x}
            disabled={disabled}
            fullWidth={fullWidth}
            renderOption={(props, option) => <li {...props}>{option.name}</li>}
            renderInput={(params) => (
                <TextField
                    {...params}
                    label={label}
                    placeholder={placeholder}
                    error={error}
                    required={required}
                    InputProps={{
                        ...params.InputProps,
                        startAdornment: (
                            <>
                                {showIcon && (
                                    <InputAdornment position="start">
                                        <LocationCity fontSize="small" color="action" />
                                    </InputAdornment>
                                )}
                                {params.InputProps.startAdornment}
                            </>
                        ),
                        endAdornment: (
                            <>
                                {isLoading ? <CircularProgress color="inherit" size={18} /> : null}
                                {params.InputProps.endAdornment}
                            </>
                        ),
                    }}
                />
            )}
        />
    );
};

export default DistrictAutocomplete;
